import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { formatMoney } from '../utils/currency';
import { apiClient } from '../utils/apiClient';
import './Invoices.css';
import { alertDialog } from '../utils/dialogs';

const FILTERS = [
  { key: 'All', label: 'All Invoices', statsKey: 'All' },
  { key: 'Paid', label: 'Paid', statsKey: 'Paid' },
  { key: 'Pending Payment', label: 'Pending Payment', statsKey: 'Payment_Pending' },
  { key: 'Payment Overdue', label: 'Payment Overdue', statsKey: 'Payment_Overdue' },
  { key: 'Draft', label: 'Draft', statsKey: 'Draft' }
];

const statusBadgeClass = (status, overdue) => {
  if (overdue) return 'status-overdue';
  switch (status) {
    case 'Paid': return 'status-paid';
    case 'Pending Payment': return 'status-pending-payment';
    case 'Draft': return 'status-draft';
    default: return 'status-default';
  }
};

const isOverdue = (invoice) => invoice.payment_status === 'Pending Payment' && invoice.payment_due_date && new Date(invoice.payment_due_date) < new Date();

// A company trades under its company name, a sole trader under their own,
// and an older record may only ever have had an email - so fall through
// rather than showing a blank cell. Matches Quotes.js.
const customerName = (customer) => {
  if (!customer) return null;
  return customer.companyName || [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email || null;
};

// What the invoice is *for*: the site its lines refer to, worked out by the
// API from the purchase lines. Most invoices have exactly one; a few cover
// several, and plenty (ad-hoc work, hosting with no site recorded) have none.
const domainLabel = (invoice) => {
  const domains = invoice.domains || [];
  if (domains.length === 0) return '—';
  if (domains.length === 1) return domains[0];
  return `${domains[0]} +${domains.length - 1} more`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).replace(',', '');
};

function Invoices() {
  const navigate = useNavigate();
  const location = useLocation();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [stats, setStats] = useState({});
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const filterFromUrl = urlParams.get('filter');
    if (filterFromUrl) setFilter(filterFromUrl);
    const userIdFromUrl = urlParams.get('user_id');

    async function fetchInvoices() {
      setLoading(true);
      try {
        setInvoices(await apiClient.post('/api/invoices/GetInvoices', userIdFromUrl ? { user_id: userIdFromUrl } : {}));
      } catch (error) {
        console.error('Error fetching invoices:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchInvoices();
  }, [location.search]);

  useEffect(() => {
    async function fetchStats() {
      try {
        setStats(await apiClient.get('/api/invoices/stats'));
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    }
    fetchStats();
  }, []);

  const filteredInvoices = invoices.filter((invoice) => {
    switch (filter) {
      case 'Paid': return invoice.payment_status === 'Paid';
      case 'Pending Payment': return invoice.payment_status === 'Pending Payment';
      case 'Payment Overdue': return isOverdue(invoice);
      case 'Draft': return invoice.payment_status === 'Draft';
      default: return true;
    }
  });

  useEffect(() => setCurrentPage(1), [filter]);

  const openPreview = async (invoiceNumber) => {
    if (previewLoading) return;
    setPreviewInvoice(invoiceNumber);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const response = await apiClient.post('/api/invoices/DisplayInvoice', { invoiceNum: invoiceNumber }, { raw: true });
      if (response.ok) {
        const blob = await response.blob();
        setPreviewUrl(window.URL.createObjectURL(blob));
      } else {
        console.error('Failed to fetch PDF');
        alertDialog('Failed to load invoice preview');
        setPreviewInvoice(null);
      }
    } catch (error) {
      console.error('Error fetching PDF:', error);
      alertDialog('Failed to load invoice preview');
      setPreviewInvoice(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewInvoice(null);
    setPreviewUrl(null);
  };

  const previewRow = invoices.find((inv) => inv.invoice_number === previewInvoice);
  const previewCustomer = customerName(previewRow?.customer);
  const previewTitle = `Invoice ${previewInvoice || ''}${previewCustomer ? ` — ${previewCustomer}` : ''}`;

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredInvoices.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / itemsPerPage));

  const columns = [
    { key: 'invoice_number', label: 'Invoice Number', sortable: true },
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      sortValue: (inv) => customerName(inv.customer) || '',
      render: (inv) => {
        const name = customerName(inv.customer);
        if (!name) return <span className="invoice-cell-muted">Unknown customer</span>;
        const email = inv.customer?.email || inv.email;
        const customerId = inv.customer?.id;
        return (
          <div className="invoice-customer-cell">
            {customerId ? (
              <span
                className="invoice-customer-name invoice-customer-link"
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); navigate(`/customer/${customerId}`); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/customer/${customerId}`); } }}
              >
                {name}
              </span>
            ) : (
              <span className="invoice-customer-name">{name}</span>
            )}
            {email && email !== name && <span className="invoice-cell-muted">{email}</span>}
          </div>
        );
      }
    },
    {
      key: 'domains',
      label: 'Domain',
      sortable: true,
      sortValue: (inv) => (inv.domains || [])[0] || '',
      render: (inv) => (
        <span
          className={inv.domains?.length ? 'invoice-domain' : 'invoice-cell-muted'}
          title={(inv.domains || []).join(', ')}
        >
          {domainLabel(inv)}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      sortValue: (inv) => inv.payment_status || '',
      render: (inv) => <span className={`status-badge ${statusBadgeClass(inv.payment_status, isOverdue(inv))}`}>{isOverdue(inv) ? 'Overdue' : inv.payment_status}</span>
    },
    { key: 'creation_date', label: 'Created Date', sortable: true, sortValue: (inv) => inv.creation_date ? new Date(inv.creation_date).getTime() : 0, render: (inv) => formatDate(inv.creation_date) },
    {
      key: 'payment_due_date',
      label: 'Due Date',
      sortable: true,
      sortValue: (inv) => inv.payment_due_date ? new Date(inv.payment_due_date).getTime() : 0,
      render: (inv) => <span className={isOverdue(inv) ? 'invoice-overdue-date' : ''}>{formatDate(inv.payment_due_date)}</span>
    },
    { key: 'paid_on', label: 'Paid On Date', sortable: true, sortValue: (inv) => inv.paid_on ? new Date(inv.paid_on).getTime() : 0, render: (inv) => inv.paid_on ? formatDate(inv.paid_on) : 'N/A' },
    { key: 'amount_payable', label: 'Total Value', sortable: true, sortValue: (inv) => Number(inv.amount_payable || 0), render: (inv) => formatMoney(inv.amount_payable, inv.currency) },
    {
      key: 'actions',
      label: 'Actions',
      render: (inv) => (
        <div className="invoice-row-actions" onClick={(e) => e.stopPropagation()}>
          <Button variant="secondary" onClick={() => openPreview(inv.invoice_number)}>Preview</Button>
          {inv.payment_status !== 'Paid' && (
            <Button variant="secondary" onClick={() => navigate(`editinvoice/${inv.invoice_number}`)}>Edit</Button>
          )}
        </div>
      )
    }
  ];

  return (
    <PageShell>
      <div className="invoices-header">
        <h1>Invoices</h1>
        <Button onClick={() => navigate('/addinvoice')}><i className="fas fa-plus"></i> Add Invoice</Button>
      </div>

      <div className="invoices-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`view-toggle-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label} ({stats[f.statsKey] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <p>Loading invoices...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={currentItems}
          rowKey="invoice_number"
          onRowClick={(inv) => openPreview(inv.invoice_number)}
          emptyMessage="No invoices match this filter."
        />
      )}

      {filteredInvoices.length > 0 && (
        <div className="invoices-pagination">
          <Button variant="secondary" onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1}>Previous</Button>
          <span>Page {currentPage} of {totalPages}</span>
          <Button variant="secondary" onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages}>Next</Button>
          <select
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="invoices-per-page"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      <Modal
        open={previewInvoice !== null}
        onClose={closePreview}
        title={previewTitle}
        size="large"
        flush
        footer={(
          <>
            {previewInvoice && (
              <Button variant="secondary" onClick={() => { closePreview(); navigate(`editinvoice/${previewInvoice}`); }}>Edit Invoice</Button>
            )}
            <Button variant="secondary" onClick={closePreview}>Close</Button>
          </>
        )}
      >
        {previewLoading && <p>Loading preview...</p>}
        {previewUrl && <iframe src={previewUrl} title="Invoice preview" className="invoice-preview-frame" />}
      </Modal>
    </PageShell>
  );
}

export default Invoices;
